import Link from 'next/link'

// Components
import BlurImage from '@/components/shared/blur-image'

// Utils
import { cn, placeholderBlurhash } from '@/lib/utils'

// Types
import type { Dictionary } from '@/types/global'
import type { Project } from '@/lib/generated/prisma/client'

export function HomeProjectCard({
  project,
  lang,
  dictionary,
  priority = false,
}: {
  project: Project
  lang: string
  dictionary: Dictionary
  priority?: boolean
}) {
  return (
    <article
      className={cn(
        'group relative h-full w-full overflow-hidden  bg-white shadow-sm transition-all duration-300 hover:shadow-lg'
      )}
    >
      <div className="min-h-[64px] sm:min-h-[80px] ">
        <h3 className="line-clamp-2 break-words font-satoshi font-bold leading-tight text-lg md:text-xl">
          {project.title}
        </h3>
      </div>

      {project.coverImage && (
        <div className="relative aspect-square sm:aspect-auto md:rounded-b-lg">
          <BlurImage
            alt={project.title}
            blurDataURL={placeholderBlurhash}
            className="size-full border-b object-cover  object-center sm:aspect-[4/3] md:aspect-[4/5] transition-transform duration-300 group-hover:scale-105"
            width={1200}
            height={600}
            priority={priority}
            placeholder="blur"
            src={project.coverImage}
          />
          <div className="absolute inset-0 bg-black/20 transition-all duration-300 group-hover:bg-black/0" />
        </div>
      )}

      {/* Decorative borders - hidden on very small screens */}
      <div className="absolute left-2 top-20 hidden w-[60px] border-t-2 border-[#d4af37] transition-all duration-300 group-hover:w-[40%] sm:block sm:top-[90px] sm:w-[80px] md:group-hover:w-[60%] group-hover:sm:top-[80px]" />
      <div className="absolute left-2 top-20 hidden h-[60px] border-l-2 border-[#d4af37] transition-all duration-300 group-hover:h-[30%] sm:block sm:top-[90px] sm:h-[80px] md:group-hover:h-[40%] group-hover:sm:top-[80px]" />
      <div className="absolute bottom-2 right-2 hidden w-[60px] border-b-2 border-[#d4af37] transition-all duration-300 group-hover:w-[40%] sm:block sm:w-[80px] md:group-hover:w-[60%]" />
      <div className="absolute bottom-2 right-2 hidden h-[60px] border-r-2 border-[#d4af37] transition-all duration-300 group-hover:h-[30%] sm:block sm:h-[80px] md:group-hover:h-[40%]" />

      <Link
        href={`/${lang}/projects/${project.slug}`}
        className="absolute inset-0 z-10"
        aria-label={`View project: ${project.title}`}
      >
        <span className="sr-only">View Project: {project.title}</span>
      </Link>
    </article>
  )
}
